import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  LinearProgress,
  Alert,
  Button,
  Autocomplete,
  TextField,
  Switch,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  CloudUpload as CloudUploadIcon,
  RecordVoiceOver as RecordVoiceOverIcon,
  Error as ErrorIcon,
  Replay as ReplayIcon,
} from '@mui/icons-material';
import { CircularProgress } from '@mui/material';
import { surface, gradients } from '../../theme';
import { formatVoiceDurationSeconds } from './utils';

export default function VoiceOverSection({
  doc,
  voiceOverConfigs,
  selectedVoiceOverConfigForGenerate,
  setSelectedVoiceOverConfigForGenerate,
  uploading,
  uploadProgress,
  generatingVoiceOver,
  generateVoiceOverError,
  onDismissGenerateVoiceOverError,
  handleVoiceoverSelect,
  handleGenerateVoiceOver,
  handleBackgroundMusicChange,
  voFileInputRef,
}) {
  return (
    <Paper
      sx={{
        p: 3,
        borderRadius: 2,
        background: surface.glass,
        boxShadow: 2,
        mb: 2,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Typography variant='h6' sx={{ fontWeight: 700, flex: 1 }}>
          Step 2: Voice over
        </Typography>
        {doc?.voiceOverObjectName && (
          <Chip
            icon={<CheckCircleIcon />}
            label='Uploaded'
            color='success'
            size='small'
            variant='outlined'
          />
        )}
        {doc?.voiceOverGenerationStatus?.status === 'queued' && (
          <Chip
            icon={<CircularProgress size={14} />}
            label='In queue'
            color='info'
            size='small'
            variant='outlined'
            sx={{ ml: 1 }}
          />
        )}
        {(doc?.voiceOverGenerationStatus?.status === 'generating' ||
          doc?.voiceOverGenerationStatus?.status === 'generating-voiceover') && (
          <Chip
            icon={<CircularProgress size={14} />}
            label='Generating...'
            color='warning'
            size='small'
            variant='outlined'
            sx={{ ml: 1 }}
          />
        )}
        {doc?.voiceOverGenerationStatus?.status === 'failed' && (
          <Chip
            icon={<ErrorIcon />}
            label='Generation failed'
            color='error'
            size='small'
            variant='outlined'
            sx={{ ml: 1 }}
          />
        )}
      </Box>
      {doc?.voiceOverGenerationStatus?.status === 'failed' &&
        doc?.voiceOverGenerationStatus?.error && (
          <Alert severity='error' sx={{ mb: 2 }} onClose={onDismissGenerateVoiceOverError}>
            {doc.voiceOverGenerationStatus.error}
          </Alert>
        )}
      {generateVoiceOverError && (
        <Alert severity='error' sx={{ mb: 2 }} onClose={onDismissGenerateVoiceOverError}>
          {generateVoiceOverError}
        </Alert>
      )}
      {doc?.voiceOverDuration && (
        <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
          Duration: {formatVoiceDurationSeconds(doc.voiceOverDuration)}
        </Typography>
      )}
      {doc?.voiceOverUrl && (
        <Box sx={{ mb: 2 }}>
          <audio controls src={doc.voiceOverUrl} style={{ width: '100%' }} />
        </Box>
      )}
      {voiceOverConfigs.length > 0 && (
        <Autocomplete
          fullWidth
          size='small'
          options={voiceOverConfigs}
          getOptionLabel={(opt) => (opt ? opt.name : '')}
          value={selectedVoiceOverConfigForGenerate}
          onChange={(_, val) => setSelectedVoiceOverConfigForGenerate(val)}
          renderInput={(params) => (
            <TextField {...params} label='Voice Over config (for Generate)' />
          )}
          sx={{ mb: 2 }}
        />
      )}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
        <Button
          variant='contained'
          startIcon={
            uploading ? (
              <CircularProgress size={18} color='inherit' />
            ) : (
              <CloudUploadIcon />
            )
          }
          onClick={() => voFileInputRef.current?.click()}
          disabled={uploading}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            boxShadow: 2,
            background: gradients.primary,
            '&:hover': { background: gradients.primaryHover },
          }}
        >
          {uploading
            ? 'Uploading...'
            : doc?.voiceOverObjectName
              ? 'Replace Voice Over'
              : 'Upload Voice Over'}
        </Button>
        <Button
          variant='outlined'
          startIcon={
            generatingVoiceOver ||
            doc?.voiceOverGenerationStatus?.status === 'queued' ||
            doc?.voiceOverGenerationStatus?.status === 'generating' ||
            doc?.voiceOverGenerationStatus?.status === 'generating-voiceover' ? (
              <CircularProgress size={18} color='inherit' />
            ) : doc?.voiceOverGenerationStatus?.status === 'failed' ? (
              <ReplayIcon />
            ) : (
              <RecordVoiceOverIcon />
            )
          }
          onClick={handleGenerateVoiceOver}
          disabled={
            generatingVoiceOver ||
            doc?.voiceOverGenerationStatus?.status === 'queued' ||
            doc?.voiceOverGenerationStatus?.status === 'generating' ||
            doc?.voiceOverGenerationStatus?.status === 'generating-voiceover' ||
            !doc?.fullScript?.trim() ||
            (!doc?.voiceOverConfigId && !selectedVoiceOverConfigForGenerate?.id)
          }
          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
        >
          {generatingVoiceOver ||
          doc?.voiceOverGenerationStatus?.status === 'generating' ||
          doc?.voiceOverGenerationStatus?.status === 'generating-voiceover'
            ? 'Generating...'
            : doc?.voiceOverGenerationStatus?.status === 'queued'
              ? 'In queue'
              : doc?.voiceOverGenerationStatus?.status === 'failed'
                ? 'Retry'
                : 'Generate Voice Over'}
        </Button>
      </Box>
      <input
        type='file'
        accept='audio/*'
        ref={voFileInputRef}
        style={{ display: 'none' }}
        onChange={handleVoiceoverSelect}
      />
      {uploading && (
        <Box sx={{ mt: 2 }}>
          <LinearProgress
            variant='determinate'
            value={uploadProgress}
            sx={{ borderRadius: 1 }}
          />
          <Typography variant='caption' color='text.secondary' sx={{ mt: 0.5, display: 'block' }}>
            {uploadProgress}%
          </Typography>
        </Box>
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
        <Switch
          checked={doc?.useBackgroundMusic === true}
          onChange={handleBackgroundMusicChange}
          color='primary'
          aria-label='Background music'
        />
        <Typography component='span' sx={{ ml: 1.5 }}>
          Background music
        </Typography>
      </Box>
    </Paper>
  );
}
